// AdminViewPdf.jsx - Use only CDN URLs
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

// Use only CDN URLs - remove local path
const WORKER_SOURCES = [
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js',
  'https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js'
];

// Set initial worker source
pdfjsLib.GlobalWorkerOptions.workerSrc = WORKER_SOURCES[0];

const apiUrl = process.env.REACT_APP_API_URL;

function AdminViewPdf() {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const canvasRef = useRef(null);
  const { bookTitle, pdfUrl, courseName } = location.state || {};
  const pdfDocumentRef = useRef(null);

  // Initialize worker on component mount
  useEffect(() => {
    let mounted = true;
    
    const initWorker = async () => {
      for (const src of WORKER_SOURCES) {
        try {
          console.log('Trying worker source:', src);
          pdfjsLib.GlobalWorkerOptions.workerSrc = src;
          
          // Wait a bit for worker to load
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Try to load a test document
          const loadingTask = pdfjsLib.getDocument({ data: new Uint8Array([37, 80, 68, 70, 45, 49, 46, 10]).buffer });
          
          await new Promise((resolve, reject) => {
            loadingTask.promise.then(
              () => reject(new Error('Should not succeed')),
              (err) => {
                if (err.message.includes('Invalid PDF') || err.message.includes('format')) {
                  resolve();
                } else {
                  reject(err);
                }
              }
            );
          });
          
          console.log('Worker initialized successfully with:', src);
          if (mounted) {
            setLoading(false);
          }
          return;
        } catch (err) {
          console.log('Worker initialization failed for:', src, err.message);
        }
      }
      
      if (mounted) {
        setError('Failed to initialize PDF viewer. Please try refreshing the page.');
        setLoading(false);
      }
    };

    initWorker();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!bookTitle || !pdfUrl) {
      setError('Book information not found. Please go back and try again.');
      setLoading(false);
      return;
    }
  }, [bookTitle, pdfUrl]);

  const loadPdf = async () => {
    try {
      setError('');
      
      // Get admin token from session
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      const token = sessionData?.token;

      if (!token) {
        throw new Error('No authentication token available. Please log in again.');
      }

      console.log('Fetching PDF from:', pdfUrl);
      
      // Fetch the PDF as array buffer
      const response = await fetch(pdfUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/pdf'
        }
      });

      console.log('PDF response status:', response.status);

      if (!response.ok) {
        const text = await response.text();
        console.error('Error response body:', text.substring(0, 200));
        throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      console.log('PDF loaded, size:', arrayBuffer.byteLength, 'bytes');
      
      // Load PDF document
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      
      loadingTask.onProgress = (progress) => {
        console.log('PDF loading progress:', progress);
      };

      const pdf = await loadingTask.promise;
      console.log('PDF document loaded, pages:', pdf.numPages);
      
      pdfDocumentRef.current = pdf;
      setPageCount(pdf.numPages);
      
      // Render first page
      if (canvasRef.current) {
        renderPage(1, pdf);
      }
    } catch (err) {
      console.error('Error loading PDF:', err);
      setError(err.message || 'Failed to load PDF');
    }
  };

  const renderPage = async (pageNumber, pdf = pdfDocumentRef.current) => {
    try {
      const canvas = canvasRef.current;
      
      if (!pdf || !canvas) {
        return;
      }

      console.log('Rendering page:', pageNumber);
      const page = await pdf.getPage(pageNumber);
      
      const viewport = page.getViewport({ scale });

      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };

      await page.render(renderContext).promise;
      console.log('Page rendered successfully');
    } catch (err) {
      console.error('Error rendering page:', err);
      setError('Failed to render page: ' + err.message);
    }
  };

  // Load PDF when worker is ready
  useEffect(() => {
    if (!loading && !error && pdfUrl) {
      loadPdf();
    }
  }, [loading, pdfUrl]);

  // Handle page changes
  useEffect(() => {
    if (pdfDocumentRef.current && canvasRef.current) {
      renderPage(currentPage);
    }
  }, [currentPage, scale]);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < pageCount) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3.0));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleRetry = () => {
    window.location.reload();
  };

  const handleOpenInNewTab = async () => {
    try {
      const token = JSON.parse(sessionStorage.getItem('adminSession'))?.token;
      const response = await fetch(pdfUrl, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error('Could not open the PDF.');
      const blob = await response.blob();
      window.open(URL.createObjectURL(blob), '_blank', 'noopener,noreferrer');
    } catch (err) {
      alert(err.message || 'Could not open the PDF.');
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const securityFunctions = {
    disableRightClick: (e) => {
      e.preventDefault();
      return false;
    },
    disableKeyboardShortcuts: (e) => {
      if (e.ctrlKey || e.metaKey) {
        if (['p', 's', 'P', 'S', 'c', 'C'].includes(e.key)) {
          e.preventDefault();
          return false;
        }
      }
      if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && e.key === 'I')) {
        e.preventDefault();
        return false;
      }
    }
  };

  // Global security effects
  useEffect(() => {
    document.addEventListener('keydown', securityFunctions.disableKeyboardShortcuts);
    document.addEventListener('contextmenu', securityFunctions.disableRightClick);

    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';

    return () => {
      document.removeEventListener('keydown', securityFunctions.disableKeyboardShortcuts);
      document.removeEventListener('contextmenu', securityFunctions.disableRightClick);
      
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
    };
  }, []);

  if (!bookTitle || !pdfUrl) {
    return (
      <>
        <SideBar />
        <div className="dashboard-main-wrapper">
          <TopNavBar />
          <div className="dashboard-body">
            <div className="alert alert-danger m-4">
              <h4>Error</h4>
              <p>Book information not found. Please go back and try again.</p>
              <button onClick={handleGoBack} className="btn btn-primary">Go Back</button>
            </div>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li><Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">Home</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><Link to="/admin-add-books" className="text-gray-200 fw-normal text-15 hover-text-main-600">Manage Books</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><span className="text-main-600 fw-normal text-15">View PDF</span></li>
              </ul>
            </div>
            <div className="flex-align gap-8 flex-wrap">
              <button onClick={handleGoBack} className="btn btn-secondary">
                <i className="ph ph-arrow-left me-2"></i> Back to Books
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center flex-wrap">
                <div>
                  <h4 className="mb-0">{bookTitle}</h4>
                  {courseName && <p className="text-muted mb-0">Course: {courseName}</p>}
                </div>
                <div className="d-flex align-items-center gap-2">
                  {!loading && !error && pageCount > 0 && (
                    <span className="badge bg-success">Page {currentPage} of {pageCount}</span>
                  )}
                  <div className="btn-group">
                    <button onClick={handleZoomOut} className="btn btn-sm btn-outline-secondary" disabled={loading || error || scale <= 0.5} title="Zoom Out">
                      <i className="ph ph-magnifying-glass-minus"></i>
                    </button>
                    <button className="btn btn-sm btn-outline-secondary" disabled>
                      {Math.round(scale * 100)}%
                    </button>
                    <button onClick={handleZoomIn} className="btn btn-sm btn-outline-secondary" disabled={loading || error || scale >= 3.0} title="Zoom In">
                      <i className="ph ph-magnifying-glass-plus"></i>
                    </button>
                  </div>
                  <button onClick={handleOpenInNewTab} className="btn btn-sm btn-info" title="Open in new tab">
                    <i className="ph ph-arrow-square-out"></i>
                  </button>
                </div>
              </div>
            </div>

            <div 
              className="card-body p-0 text-center"
              style={{ height: '70vh', overflow: 'auto', backgroundColor: '#f5f5f5', display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}
              onContextMenu={securityFunctions.disableRightClick}
              onKeyDown={securityFunctions.disableKeyboardShortcuts}
              tabIndex="0"
            >
              {error && (
                <div className="alert alert-danger m-4 w-100">
                  <h5>Error Loading PDF</h5>
                  <p>{error}</p>
                  <div className="mt-3">
                    <button onClick={handleRetry} className="btn btn-primary me-2">
                      <i className="ph ph-arrow-clockwise me-2"></i> Retry
                    </button>
                    <button onClick={handleOpenInNewTab} className="btn btn-info me-2">
                      <i className="ph ph-arrow-square-out me-2"></i> Open in New Tab
                    </button>
                    <button onClick={handleGoBack} className="btn btn-secondary">
                      <i className="ph ph-arrow-left me-2"></i> Go Back
                    </button>
                  </div>
                </div>
              )}

              {loading && !error && (
                <div className="text-center p-5 w-100">
                  <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                    <span className="visually-hidden">Loading PDF...</span>
                  </div>
                  <p className="mt-3">Loading PDF viewer...</p>
                </div>
              )}

              {!loading && !error && (
                <div style={{ padding: '20px', minWidth: '100%' }}>
                  <canvas ref={canvasRef} style={{ maxWidth: '100%', height: 'auto', boxShadow: '0 4px 8px rgba(0,0,0,0.1)', display: 'block', margin: '0 auto' }} />
                </div>
              )}
            </div>

            <div className="card-footer">
              <div className="d-flex justify-content-between align-items-center flex-wrap">
                <small className="text-muted"><i className="ph ph-file-pdf me-1"></i>{bookTitle}</small>
                {!loading && !error && pageCount > 0 && (
                  <div className="d-flex gap-2">
                    <button onClick={handlePrevPage} className="btn btn-sm btn-primary" disabled={currentPage <= 1}>
                      <i className="ph ph-caret-left me-1"></i> Previous
                    </button>
                    <span className="btn btn-sm btn-outline-secondary disabled">{currentPage} / {pageCount}</span>
                    <button onClick={handleNextPage} className="btn btn-sm btn-primary" disabled={currentPage >= pageCount}>
                      Next <i className="ph ph-caret-right ms-1"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminViewPdf;

// // In AdminViewPdf.jsx - Use a different approach
// import React, { useState, useEffect } from 'react';
// import { Link, useLocation, useNavigate } from 'react-router-dom';
// import SideBar from '../admincomponents/SideBar';
// import TopNavBar from '../admincomponents/TopNavBar';
// import Footer from '../admincomponents/Footer';

// const apiUrl = process.env.REACT_APP_API_URL;

// function AdminViewPdf() {
//   const location = useLocation();
//   const navigate = useNavigate();
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState('');
//   const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
//   const { bookTitle, pdfUrl, courseName } = location.state || {};

//   useEffect(() => {
//     if (!bookTitle || !pdfUrl) {
//       setError('Book information not found. Please go back and try again.');
//       setLoading(false);
//       return;
//     }

//     // Fetch PDF as blob and create object URL
//     fetchPdfAsBlob();
//   }, [pdfUrl]);

//   const fetchPdfAsBlob = async () => {
//     try {
//       setLoading(true);
//       const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
//       const token = sessionData?.token;

//       if (!token) {
//         throw new Error('No authentication token available');
//       }

//       const response = await fetch(pdfUrl, {
//         headers: {
//           'Authorization': `Bearer ${token}`
//         }
//       });

//       if (!response.ok) {
//         throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
//       }

//       const blob = await response.blob();
      
//       if (blob.type !== 'application/pdf') {
//         throw new Error('Invalid file type');
//       }

//       const blobUrl = URL.createObjectURL(blob);
//       setPdfBlobUrl(blobUrl);
//       setLoading(false);
//     } catch (err) {
//       console.error('Error fetching PDF:', err);
//       setError(err.message || 'Failed to load PDF');
//       setLoading(false);
//     }
//   };

//   // Security functions
//   const disableRightClick = (e) => {
//     e.preventDefault();
//     return false;
//   };

//   const disableTextSelection = (e) => {
//     e.preventDefault();
//     return false;
//   };

//   const disableKeyboardShortcuts = (e) => {
//     if (e.ctrlKey || e.metaKey) {
//       if (['p', 's', 'P', 'S'].includes(e.key)) {
//         e.preventDefault();
//         e.stopPropagation();
//         return false;
//       }
//     }
//     if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && e.key === 'I')) {
//       e.preventDefault();
//       return false;
//     }
//   };

//   const handleGoBack = () => {
//     navigate(-1);
//   };

//   // Clean up blob URL on unmount
//   useEffect(() => {
//     return () => {
//       if (pdfBlobUrl) {
//         URL.revokeObjectURL(pdfBlobUrl);
//       }
//     };
//   }, [pdfBlobUrl]);

//   // Render PDF viewer using blob URL
//   const renderPdfViewer = () => {
//     if (!pdfBlobUrl) return null;

//     const securedUrl = `${pdfBlobUrl}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&view=FitH`;
    
//     return (
//       <div style={{ position: 'relative', width: '100%', height: '100%' }}>
//         <embed
//           src={securedUrl}
//           type="application/pdf"
//           width="100%"
//           height="100%"
//           onContextMenu={disableRightClick}
//         />
        
//         {/* Security overlay */}
//         <div 
//           style={{
//             position: 'absolute',
//             top: 0,
//             left: 0,
//             width: '100%',
//             height: '100%',
//             pointerEvents: 'none',
//             zIndex: 1
//           }}
//           onContextMenu={disableRightClick}
//         />
//       </div>
//     );
//   };

//   // Global security effects
//   useEffect(() => {
//     const disablePrint = (e) => {
//       e.preventDefault();
//       return false;
//     };

//     window.addEventListener('beforeprint', disablePrint);
//     document.addEventListener('keydown', disableKeyboardShortcuts);
//     document.addEventListener('contextmenu', disableRightClick);
//     document.addEventListener('selectstart', disableTextSelection);

//     document.body.style.userSelect = 'none';
//     document.body.style.webkitUserSelect = 'none';
//     document.body.style.mozUserSelect = 'none';
//     document.body.style.msUserSelect = 'none';

//     return () => {
//       window.removeEventListener('beforeprint', disablePrint);
//       document.removeEventListener('keydown', disableKeyboardShortcuts);
//       document.removeEventListener('contextmenu', disableRightClick);
//       document.removeEventListener('selectstart', disableTextSelection);
      
//       document.body.style.userSelect = '';
//       document.body.style.webkitUserSelect = '';
//       document.body.style.mozUserSelect = '';
//       document.body.style.msUserSelect = '';
//     };
//   }, []);

//   if (!bookTitle || !pdfUrl) {
//     return (
//       <>
//         <SideBar />
//         <div className="dashboard-main-wrapper">
//           <TopNavBar />
//           <div className="dashboard-body">
//             <div className="alert alert-danger m-4">
//               <h4>Error</h4>
//               <p>Book information not found. Please go back and try again.</p>
//               <button onClick={handleGoBack} className="btn btn-primary">
//                 Go Back
//               </button>
//             </div>
//           </div>
//           <Footer />
//         </div>
//       </>
//     );
//   }

//   return (
//     <>
//       <SideBar />
//       <div className="dashboard-main-wrapper">
//         <TopNavBar />
//         <div className="dashboard-body">
//           <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
//             <div className="breadcrumb mb-24">
//               <ul className="flex-align gap-4">
//                 <li>
//                   <Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">
//                     Home
//                   </Link>
//                 </li>
//                 <li>
//                   <span className="text-gray-500 fw-normal d-flex">
//                     <i className="ph ph-caret-right" />
//                   </span>
//                 </li>
//                 <li>
//                   <Link to="/admin-add-books" className="text-gray-200 fw-normal text-15 hover-text-main-600">
//                     Manage Books
//                   </Link>
//                 </li>
//                 <li>
//                   <span className="text-gray-500 fw-normal d-flex">
//                     <i className="ph ph-caret-right" />
//                   </span>
//                 </li>
//                 <li>
//                   <span className="text-main-600 fw-normal text-15">View PDF</span>
//                 </li>
//               </ul>
//             </div>
//             <div className="flex-align gap-8 flex-wrap">
//               <button onClick={handleGoBack} className="btn btn-secondary">
//                 <i className="ph ph-arrow-left me-2"></i>
//                 Back to Books
//               </button>
//               <a 
//                 href={pdfBlobUrl || pdfUrl} 
//                 target="_blank" 
//                 rel="noopener noreferrer"
//                 className="btn btn-primary"
//                 download={`${bookTitle}.pdf`}
//               >
//                 <i className="ph ph-arrow-square-out me-2"></i>
//                 Open in New Tab
//               </a>
//             </div>
//           </div>

//           <div className="card">
//             <div className="card-header">
//               <div className="d-flex justify-content-between align-items-center flex-wrap">
//                 <div>
//                   <h4 className="mb-0">{bookTitle}</h4>
//                   {courseName && <p className="text-muted mb-0">Course: {courseName}</p>}
//                 </div>
//                 <div className="d-flex align-items-center gap-2 mt-2 mt-md-0">
//                   <span className="badge bg-success">
//                     <i className="ph ph-book-open me-1"></i>
//                     Secure View
//                   </span>
//                   <span className="badge bg-warning">
//                     <i className="ph ph-lock me-1"></i>
//                     Protected
//                   </span>
//                 </div>
//               </div>
//             </div>

//             <div 
//               className="card-body p-0" 
//               style={{ height: '80vh' }}
//               onContextMenu={disableRightClick}
//               onSelectStart={disableTextSelection}
//               onKeyDown={disableKeyboardShortcuts}
//               tabIndex="0"
//             >
//               {error && (
//                 <div className="alert alert-danger m-4">
//                   <h5>Error Loading PDF</h5>
//                   <p>{error}</p>
//                   <div className="mt-3">
//                     <button onClick={handleGoBack} className="btn btn-primary me-2">
//                       Go Back
//                     </button>
//                     <button onClick={fetchPdfAsBlob} className="btn btn-secondary">
//                       Retry Loading
//                     </button>
//                   </div>
//                 </div>
//               )}

//               {loading && !error && (
//                 <div className="text-center p-5">
//                   <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
//                     <span className="visually-hidden">Loading book...</span>
//                   </div>
//                   <p className="mt-3">Loading secure PDF viewer...</p>
//                   <small className="text-muted d-block mt-2">
//                     Downloading and securing PDF content
//                   </small>
//                 </div>
//               )}

//               {!loading && !error && pdfBlobUrl && renderPdfViewer()}
//             </div>

//             <div className="card-footer">
//               <div className="d-flex justify-content-between align-items-center flex-wrap">
//                 <small className="text-muted">
//                   <i className="ph ph-book me-1"></i>
//                   {bookTitle}
//                 </small>
//                 <div className="d-flex gap-2 mt-2 mt-md-0">
//                   <span className="badge bg-light text-dark">
//                     <i className="ph ph-eye me-1"></i>
//                     View Only
//                   </span>
//                   <button onClick={handleGoBack} className="btn btn-secondary btn-sm">
//                     <i className="ph ph-arrow-left me-2"></i>
//                     Back to Books
//                   </button>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//         <Footer />
//       </div>
//     </>
//   );
// }

// export default AdminViewPdf;